// src/components/modals/CreateAttendanceModal.jsx
import { useState, useEffect } from 'react'
import { X, Calendar, Clock, User, ChevronDown } from 'lucide-react'
import { format } from 'date-fns'
import toast from 'react-hot-toast'

const CreateAttendanceModal = ({ isOpen, onClose, onSubmit, loading, employees = [], existingAttendances = [] }) => {
  const [formData, setFormData] = useState({
    employee: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    clockIn: '',
    clockOut: '',
    status: 'present'
  })
  const [errors, setErrors] = useState({})

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        employee: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        clockIn: '',
        clockOut: '',
        status: 'present'
      })
      setErrors({})
    }
  }, [isOpen])

  const validateForm = () => {
    const newErrors = {}

    if (!formData.employee) {
      newErrors.employee = 'Employee is required'
    }

    if (!formData.date) {
      newErrors.date = 'Date is required'
    } else {
      // Check if the selected date is in the future
      const selectedDate = new Date(formData.date)
      const today = new Date()
      
      // Reset time to compare only dates
      selectedDate.setHours(0, 0, 0, 0)
      today.setHours(0, 0, 0, 0)
      
      if (selectedDate > today) {
        newErrors.date = 'Cannot create attendance for future dates. Please select today or a past date.'
      }
    }

    // Check for duplicate attendance record
    if (formData.employee && formData.date && !newErrors.date) {
      const duplicate = existingAttendances.find(attendance => {
        // Compare employee ID
        const employeeMatch = attendance.employee._id === formData.employee || attendance.employee === formData.employee
        
        // Compare dates - handle different date formats
        let dateMatch = false
        try {
          const existingDate = new Date(attendance.date).toISOString().split('T')[0]
          const selectedDate = formData.date
          dateMatch = existingDate === selectedDate
        } catch {
          // Fallback to string comparison
          dateMatch = attendance.date === formData.date
        }
        
        return employeeMatch && dateMatch
      })
      
      if (duplicate) {
        newErrors.date = `Attendance record already exists for this employee on the selected date. You can edit the existing record instead.`
      }
    }

    // Clock-in validation
    if (formData.clockIn && !/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(formData.clockIn)) {
      newErrors.clockIn = 'Clock-in must be in HH:MM format'
    }

    // Clock-out validation
    if (formData.clockOut && !/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(formData.clockOut)) {
      newErrors.clockOut = 'Clock-out must be in HH:MM format'
    }

    // Check if clock-out is after clock-in
    if (formData.clockIn && formData.clockOut) {
      const [clockInHour, clockInMin] = formData.clockIn.split(':').map(Number)
      const [clockOutHour, clockOutMin] = formData.clockOut.split(':').map(Number)
      
      const clockInMinutes = clockInHour * 60 + clockInMin
      const clockOutMinutes = clockOutHour * 60 + clockOutMin
      
      if (clockOutMinutes <= clockInMinutes) {
        newErrors.clockOut = 'Clock-out must be after clock-in time'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error('Please fix the errors in the form')
      return
    }

    // Prepare data for submission
    const submitData = {
      employee: formData.employee,
      date: formData.date,
      status: formData.status
    }

    // Add optional fields
    if (formData.clockIn) submitData.clockIn = formData.clockIn
    if (formData.clockOut) submitData.clockOut = formData.clockOut

    try {
      await onSubmit(submitData)
    } catch (error) {
      console.error('Form submission error - Full error object:', error)
      console.error('Error message:', error?.message)
      console.error('Error response:', error?.response)
      console.error('Error response data:', error?.response?.data)
      
      // Extract the actual backend error message
      let errorMessage = 'Failed to create attendance'
      
      // Try different ways to get the actual error message
      if (error?.response?.data?.message) {
        // Backend API error message
        errorMessage = error.response.data.message
        console.log('Using error.response.data.message:', errorMessage)
      } else if (error?.response?.data?.error) {
        // Alternative backend error format
        errorMessage = error.response.data.error
        console.log('Using error.response.data.error:', errorMessage)
      } else if (error?.message) {
        // Direct error message
        errorMessage = error.message
        console.log('Using error.message:', errorMessage)
      } else if (typeof error === 'string') {
        // String error
        errorMessage = error
        console.log('Using string error:', errorMessage)
      }
      
      console.log('Final error message for toast:', errorMessage)
      
      // Show backend error in toast
      toast.error(errorMessage)
      
      // If it's a validation error, try to set field-specific errors
      if (error.response?.data?.errors) {
        const backendErrors = {}
        const errors = error.response.data.errors
        
        Object.keys(errors).forEach(field => {
          backendErrors[field] = errors[field]
        })
        
        setErrors(prev => ({ ...prev, ...backendErrors }))
      } else if (errorMessage.includes('No active EmployeeShift')) {
        // Set employee field error for shift-related issues
        setErrors(prev => ({ 
          ...prev, 
          employee: errorMessage // Show exact error message
        }))
      }
    }
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">Create New Attendance</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form - Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Info Message */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-700 mb-2">
              <strong>Note:</strong> Each employee can have only one attendance record per date. 
              Attendance can only be marked for today or past dates - future dates are not allowed.
            </p>
            <p className="text-sm text-blue-700">
              <strong>Important:</strong> The employee must have an active shift assigned before attendance can be created.
            </p>
          </div>

          {/* Employee Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employee *
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <select
                value={formData.employee}
                onChange={(e) => handleChange('employee', e.target.value)}
                className={`pl-10 pr-10 py-2 w-full border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white ${
                  errors.employee ? 'border-red-300' : 'border-gray-300'
                }`}
                required
              >
                <option value="">Select employee</option>
                {employees
                  .filter(employee => employee.isActive !== false) // Only show active employees
                  .map(employee => (
                  <option key={employee._id} value={employee._id}>
                    {employee.fullName}
                    {employee.hasActiveShift === false ? ' (No Active Shift)' : ''}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
            </div>
            {errors.employee && <p className="mt-1 text-sm text-red-600">{errors.employee}</p>}
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date *
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                max={format(new Date(), 'yyyy-MM-dd')}
                className={`pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.date ? 'border-red-300' : 'border-gray-300'
                }`}
                required
              />
            </div>
            {errors.date && <p className="mt-1 text-sm text-red-600">{errors.date}</p>}
          </div>

          {/* Clock-in */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Clock-in
            </label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="time"
                value={formData.clockIn}
                onChange={(e) => handleChange('clockIn', e.target.value)}
                className={`pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.clockIn ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="HH:MM"
              />
            </div>
            {errors.clockIn && <p className="mt-1 text-sm text-red-600">{errors.clockIn}</p>}
          </div>

          {/* Clock-out */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Clock-out
            </label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="time"
                value={formData.clockOut}
                onChange={(e) => handleChange('clockOut', e.target.value)}
                className={`pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.clockOut ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="HH:MM"
              />
            </div>
            {errors.clockOut && <p className="mt-1 text-sm text-red-600">{errors.clockOut}</p>}
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <div className="relative">
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="w-full px-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white"
              >
                <option value="present">Present</option>
                <option value="absent">Absent</option>
                <option value="on_leave">On Leave</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Attendance'}
            </button>
          </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CreateAttendanceModal
